// Codey Frontend - Login and Chat UI (with conversations)

type Role = "user" | "assistant";

type Message = {
  id?: number;
  role: Role;
  content: string;
  createdAt?: number;
};

type Conversation = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
};

// DOM Elements
const loginView = document.getElementById("login-view") as HTMLDivElement;
const chatView = document.getElementById("chat-view") as HTMLDivElement;
const loginForm = document.getElementById("login-form") as HTMLFormElement;
const passwordInput = document.getElementById("password-input") as HTMLInputElement;
const loginError = document.getElementById("login-error") as HTMLParagraphElement;
const logoutBtn = document.getElementById("logout-btn") as HTMLButtonElement;
const messagesContainer = document.getElementById("messages") as HTMLDivElement;
const chatForm = document.getElementById("chat-form") as HTMLFormElement;
const chatInput = document.getElementById("chat-input") as HTMLTextAreaElement;
const sendBtn = document.getElementById("send-btn") as HTMLButtonElement;
const conversationListEl = document.getElementById("conversation-list") as HTMLDivElement;
const newChatBtn = document.getElementById("new-chat-btn") as HTMLButtonElement;
const alertBanner = document.getElementById("alert-banner") as HTMLDivElement;
const alertText = document.getElementById("alert-text") as HTMLSpanElement;
const alertDismiss = document.getElementById("alert-dismiss") as HTMLButtonElement;
const statusPanel = document.getElementById("status-panel") as HTMLDivElement;
const statusText = document.getElementById("status-text") as HTMLSpanElement;

// State
let conversations: Conversation[] = [];
let currentConversationId: string | null = null;
let messages: Message[] = [];
let lastMessageId = 0;
let pollTimer: number | null = null;
let awaitingResponse = false;
let notificationReady = false;
let statusTimer: number | null = null;

async function checkAuth(): Promise<boolean> {
  try {
    const response = await fetch("/api/auth/status");
    const data = (await response.json()) as { authenticated?: boolean };
    return data.authenticated === true;
  } catch {
    return false;
  }
}

function showView(authenticated: boolean): void {
  if (authenticated) {
    loginView.classList.add("hidden");
    chatView.classList.remove("hidden");
    chatInput.focus();
  } else {
    loginView.classList.remove("hidden");
    chatView.classList.add("hidden");
    passwordInput.focus();
  }
}

function hideAlert(): void {
  alertBanner.classList.add("hidden");
}

function showAlert(text: string): void {
  alertText.textContent = text;
  alertBanner.classList.remove("hidden");
}

function showStatus(text: string): void {
  statusText.textContent = text;
  statusPanel.classList.remove("hidden");
}

function hideStatus(): void {
  statusPanel.classList.add("hidden");
  if (statusTimer) {
    clearTimeout(statusTimer);
    statusTimer = null;
  }
}

function scheduleStatus(text: string, delayMs: number): void {
  if (statusTimer) clearTimeout(statusTimer);
  statusTimer = window.setTimeout(() => {
    showStatus(text);
  }, delayMs);
}

async function ensureNotificationPermission(): Promise<void> {
  if (!("Notification" in window)) return;
  if (Notification.permission === "granted") {
    notificationReady = true;
    return;
  }
  if (Notification.permission === "denied") return;

  const permission = await Notification.requestPermission();
  notificationReady = permission === "granted";
}

function fireBrowserNotification(title: string, body: string): void {
  if (!notificationReady) return;
  if (document.visibilityState === "visible") return;

  try {
    const note = new Notification(title, { body });
    note.onclick = () => {
      window.focus();
      note.close();
    };
  } catch {
    // ignore
  }
}

function renderMessageContent(messageDiv: HTMLDivElement, role: Role, content: string): void {
  if (role === "assistant" && typeof (window as any).marked?.parse === "function") {
    messageDiv.innerHTML = (window as any).marked.parse(content, {
      mangle: false,
      headerIds: false,
    });
  } else {
    messageDiv.textContent = content;
  }
}

function addMessageToUI(role: Role, content: string, streaming = false): HTMLDivElement {
  const messageDiv = document.createElement("div");
  messageDiv.className = `message ${role}${streaming ? " streaming" : ""}`;
  renderMessageContent(messageDiv, role, content);
  messagesContainer.appendChild(messageDiv);
  messagesContainer.scrollTop = messagesContainer.scrollHeight;
  return messageDiv;
}

function renderMessages(): void {
  messagesContainer.innerHTML = "";
  for (const m of messages) {
    addMessageToUI(m.role, m.content);
  }
}

function formatTime(ms: number): string {
  try {
    const d = new Date(ms);
    return d.toLocaleString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function renderConversationList(): void {
  conversationListEl.innerHTML = "";

  if (conversations.length === 0) {
    const empty = document.createElement("div");
    empty.style.padding = "0.75rem";
    empty.style.color = "var(--text-secondary)";
    empty.textContent = "No conversations yet.";
    conversationListEl.appendChild(empty);
    return;
  }

  for (const c of conversations) {
    const btn = document.createElement("button");
    btn.className = `conversation-item${c.id === currentConversationId ? " active" : ""}`;

    const title = document.createElement("div");
    title.className = "title";
    title.textContent = c.title || "Untitled";

    const meta = document.createElement("div");
    meta.className = "meta";
    meta.textContent = formatTime(c.updatedAt);

    btn.appendChild(title);
    btn.appendChild(meta);

    btn.addEventListener("click", async () => {
      await openConversation(c.id);
    });

    conversationListEl.appendChild(btn);
  }
}

async function fetchConversations(): Promise<void> {
  const res = await fetch("/api/conversations?limit=200");
  if (!res.ok) throw new Error("Failed to load conversations");
  const data = (await res.json()) as { conversations?: Conversation[] };
  conversations = Array.isArray(data.conversations) ? data.conversations : [];
}

async function fetchMessages(conversationId: string): Promise<void> {
  const res = await fetch(`/api/conversation/messages?conversationId=${encodeURIComponent(conversationId)}&limit=4000`);
  if (!res.ok) throw new Error("Failed to load messages");
  const data = (await res.json()) as { messages?: Message[] };
  messages = Array.isArray(data.messages) ? (data.messages as Message[]) : [];
  const last = messages[messages.length - 1];
  lastMessageId = last?.id || 0;
}

async function createNewConversation(): Promise<string> {
  const res = await fetch("/api/conversations", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
  if (!res.ok) throw new Error("Failed to create conversation");
  const data = (await res.json()) as { conversation?: Conversation };
  if (!data.conversation?.id) throw new Error("Bad conversation response");
  return data.conversation.id;
}

async function openConversation(conversationId: string): Promise<void> {
  currentConversationId = conversationId;
  await fetchConversations();
  renderConversationList();

  await fetchMessages(conversationId);
  renderMessages();
  hideAlert();
  schedulePoll(0);
}

async function bootstrapChat(): Promise<void> {
  await fetchConversations();

  if (conversations.length === 0) {
    currentConversationId = await createNewConversation();
    await fetchConversations();
  } else {
    currentConversationId = conversations[0]?.id || null;
  }

  renderConversationList();

  if (currentConversationId) {
    await fetchMessages(currentConversationId);
    renderMessages();
  }
}

async function handleLogin(event: Event): Promise<void> {
  event.preventDefault();
  loginError.textContent = "";

  const password = passwordInput.value;
  if (!password) return;

  try {
    const response = await fetch("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });

    const data = (await response.json()) as { error?: string };

    if (response.ok) {
      passwordInput.value = "";
      showView(true);
      await ensureNotificationPermission();
      await bootstrapChat();
    } else {
      loginError.textContent = data.error || "Login failed";
      passwordInput.select();
    }
  } catch {
    loginError.textContent = "Connection error. Please try again.";
  }
}

async function handleLogout(): Promise<void> {
  try {
    await fetch("/api/logout", { method: "POST" });
    conversations = [];
    currentConversationId = null;
    messages = [];
    lastMessageId = 0;
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    hideAlert();
    hideStatus();
    messagesContainer.innerHTML = "";
    conversationListEl.innerHTML = "";
    showView(false);
  } catch (error) {
    console.error("Logout error:", error);
  }
}

async function handleSendMessage(event: Event): Promise<void> {
  event.preventDefault();

  const content = chatInput.value.trim();
  if (!content) return;
  if (!currentConversationId) return;

  chatInput.value = "";
  chatInput.disabled = true;
  sendBtn.disabled = true;
  awaitingResponse = true;
  schedulePoll(0);

  // Optimistically render user message
  messages.push({ id: 0, role: "user", content, createdAt: Date.now() });
  addMessageToUI("user", content);

  showStatus("Sending message…");
  scheduleStatus("Thinking…", 800);

  const assistantDiv = addMessageToUI("assistant", "", true);
  let assistantContent = "";
  let gotFirstToken = false;

  try {
    const response = await fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ conversationId: currentConversationId, message: content }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        showView(false);
        return;
      }
      throw new Error(`Request failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("No response body");

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6);
        if (data === "[DONE]") continue;

        try {
          const parsed = JSON.parse(data);
          const delta = parsed.choices?.[0]?.delta?.content;
          if (typeof delta === "string" && delta.length > 0) {
            if (!gotFirstToken) {
              gotFirstToken = true;
              showStatus("Responding…");
            }
            assistantContent += delta;
            renderMessageContent(assistantDiv, "assistant", assistantContent);
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
          }
        } catch {
          // ignore
        }
      }
    }

    assistantDiv.classList.remove("streaming");
    messages.push({ id: 0, role: "assistant", content: assistantContent, createdAt: Date.now() });

    showStatus("Done");
    statusTimer = window.setTimeout(hideStatus, 1200);

    // Refresh conversation list ordering/timestamps.
    await fetchConversations();
    renderConversationList();
  } catch (error) {
    console.error("Chat error:", error);
    assistantDiv.classList.remove("streaming");
    assistantDiv.textContent = "Error: Failed to get response. Please try again.";
    showStatus("Error while responding");
    statusTimer = window.setTimeout(hideStatus, 2000);
  } finally {
    awaitingResponse = false;
    chatInput.disabled = false;
    sendBtn.disabled = false;
    chatInput.focus();
    schedulePoll(0);
  }
}

async function pollMessages(): Promise<void> {
  if (!currentConversationId) return;

  try {
    const response = await fetch(
      `/api/conversation/poll?conversationId=${encodeURIComponent(currentConversationId)}&afterId=${lastMessageId}&limit=200`
    );

    if (!response.ok) {
      if (response.status === 401) {
        showView(false);
      }
      return;
    }

    const data = (await response.json()) as { messages?: Message[] };
    const newMessages = Array.isArray(data.messages) ? (data.messages as Message[]) : [];
    if (newMessages.length === 0) return;

    let changed = false;
    let newAssistant = false;

    for (const msg of newMessages) {
      if (msg.id && msg.id <= lastMessageId) continue;

      let matched = false;
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const existing = messages[i];
        if ((!existing.id || existing.id <= 0) && existing.role === msg.role && existing.content === msg.content) {
          existing.id = msg.id;
          existing.createdAt = msg.createdAt;
          matched = true;
          changed = true;
          break;
        }
      }

      if (!matched) {
        messages.push(msg);
        changed = true;
      }

      if (msg.role === "assistant") {
        newAssistant = true;
      }

      if (msg.id) lastMessageId = msg.id;
    }

    if (changed) {
      renderMessages();
    }

    if (newAssistant) {
      showAlert("New message from Codey");
      fireBrowserNotification("Codey", "New message received");
    }
  } catch {
    // ignore polling errors
  }
}

function schedulePoll(delayMs: number): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
  }

  pollTimer = window.setTimeout(async () => {
    await pollMessages();
    const nextDelay = awaitingResponse ? 1000 : 5000;
    schedulePoll(nextDelay);
  }, delayMs);
}

function handleTextareaInput(): void {
  chatInput.style.height = "auto";
  chatInput.style.height = Math.min(chatInput.scrollHeight, 150) + "px";
}

function handleTextareaKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    chatForm.requestSubmit();
  }
}

async function init(): Promise<void> {
  loginForm.addEventListener("submit", handleLogin);
  logoutBtn.addEventListener("click", handleLogout);
  alertDismiss.addEventListener("click", hideAlert);
  chatForm.addEventListener("submit", handleSendMessage);
  chatInput.addEventListener("input", handleTextareaInput);
  chatInput.addEventListener("keydown", handleTextareaKeydown);

  newChatBtn.addEventListener("click", async () => {
    try {
      const id = await createNewConversation();
      await openConversation(id);
    } catch (e) {
      console.error(e);
    }
  });

  const authenticated = await checkAuth();
  showView(authenticated);

  if (authenticated) {
    try {
      await ensureNotificationPermission();
      await bootstrapChat();
      schedulePoll(0);
    } catch (e) {
      console.error(e);
    }
  }
}

init();
